/**
 * AbilityVFX — one-shot particle bursts hooked to ability events.
 *
 * Listens on the typed EventBus for ability firings and emits short-lived
 * particle systems at the relevant world position. Used by every level via
 * BaseLevel, so every ability gains visible feedback without per-level
 * wiring.
 *
 * Design notes:
 *   - Each burst gets its own ParticleSystem instance with targetStopDuration
 *     set so Babylon cleans it up automatically.
 *   - Sprites are drawn into a single DynamicTexture once (soft radial gradient)
 *     and shared across all bursts — no asset file dependency.
 *   - Positions come from event payloads where available, else from the player
 *     mesh passed to the constructor.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { AccessibilitySystem } from "./AccessibilitySystem";
import { particleDensityFactor } from "./particleScaling";
import { a11yColor4, a11yColor4FromRgb } from "./abilityColorHelpers";

/** Shared radial-gradient sprite so we don't redraw per burst. */
function makeSoftSprite(scene: Scene): Texture {
  const size = 64;
  const dt = new DynamicTexture("abilityVfxSprite", size, scene, false);
  const ctx = dt.getContext();
  const g = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.5, "rgba(255,255,255,0.55)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  dt.update(false);
  dt.hasAlpha = true;
  return dt;
}

interface BurstSpec {
  emitter: Vector3;
  /** Number of particles to spawn (approximate). */
  count: number;
  /** How long the emitter runs before auto-stopping. Seconds. */
  durationSec: number;
  /** Colors (Color4: rgba with alpha). */
  color1: Color4;
  color2: Color4;
  colorDead: Color4;
  minSize: number;
  maxSize: number;
  minLifeTime: number;
  maxLifeTime: number;
  minEmitPower: number;
  maxEmitPower: number;
  /** Emission cone half-angle. 0 = outward radial sphere. */
  direction1: Vector3;
  direction2: Vector3;
  gravity?: Vector3;
  blendMode?: number;
  emitRate: number;
}

export class AbilityVFX {
  private scene: Scene;
  private minna: AbstractMesh;
  private basil: AbstractMesh;
  private sprite: Texture;
  private unsubs: Array<() => void> = [];
  private disposed = false;
  /** Active one-shot systems awaiting auto-dispose. */
  private pool: ParticleSystem[] = [];

  constructor(scene: Scene, minna: AbstractMesh, basil: AbstractMesh) {
    this.scene = scene;
    this.minna = minna;
    this.basil = basil;
    this.sprite = makeSoftSprite(scene);
    this.wireEvents();
  }

  private wireEvents(): void {
    // Basil — brass sparks on a successful Turn-Table Touch hit.
    this.unsubs.push(
      gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, ({ targetPosition, combo }) => {
        const emitter = new Vector3(targetPosition.x, targetPosition.y, targetPosition.z);
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        this.burst({
          emitter,
          count: 22 + Math.min(combo, 6) * 4,
          durationSec: 0.18,
          color1: a11yColor4FromRgb('BRASS', 1.0, 0.82, 0.4, 1.0, rc),
          color2: a11yColor4FromRgb('COPPER', 1.0, 0.62, 0.2, 1.0, rc),
          colorDead: new Color4(0.6, 0.3, 0.05, 0),
          minSize: 0.05,
          maxSize: 0.14,
          minLifeTime: 0.25,
          maxLifeTime: 0.45,
          minEmitPower: 2.2,
          maxEmitPower: 4.8,
          direction1: new Vector3(-1.2, 0.4, -0.6),
          direction2: new Vector3(1.2, 1.6, 0.6),
          gravity: new Vector3(0, -6, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 240,
        });
      }),
    );

    // Basil — red sputter on a miss so the player reads the failure quickly.
    this.unsubs.push(
      gameEvents.on(GameEvents.TURN_TABLE_TOUCH_MISS, ({ targetPosition }) => {
        const emitter = new Vector3(targetPosition.x, targetPosition.y, targetPosition.z);
        this.burst({
          emitter,
          count: 14,
          durationSec: 0.22,
          color1: new Color4(1.0, 0.25, 0.15, 1.0),
          color2: new Color4(0.6, 0.1, 0.08, 1.0),
          colorDead: new Color4(0.2, 0.02, 0.02, 0),
          minSize: 0.06,
          maxSize: 0.12,
          minLifeTime: 0.25,
          maxLifeTime: 0.45,
          minEmitPower: 1.2,
          maxEmitPower: 2.8,
          direction1: new Vector3(-0.6, 0.2, -0.3),
          direction2: new Vector3(0.6, 1.0, 0.3),
          gravity: new Vector3(0, -7, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 160,
        });
      }),
    );

    // Basil — dust ring on Overclocked Leap landing / fire.
    this.unsubs.push(
      gameEvents.on(GameEvents.OVERCLOCKED_LEAP_FIRED, ({ position }) => {
        const emitter = new Vector3(position.x, position.y, position.z);
        this.burst({
          emitter,
          count: 40,
          durationSec: 0.3,
          color1: new Color4(0.82, 0.68, 0.44, 0.85),  // dust
          color2: new Color4(0.55, 0.4, 0.22, 0.75),
          colorDead: new Color4(0.25, 0.18, 0.1, 0),
          minSize: 0.08,
          maxSize: 0.24,
          minLifeTime: 0.45,
          maxLifeTime: 0.75,
          minEmitPower: 1.6,
          maxEmitPower: 3.4,
          direction1: new Vector3(-1.4, 0.2, -0.3),
          direction2: new Vector3(1.4, 0.8, 0.3),
          gravity: new Vector3(0, -4, 0),
          blendMode: ParticleSystem.BLENDMODE_STANDARD,
          emitRate: 300,
        });
      }),
    );

    // Minna — expanding scan pulse when Aura Sight activates.
    // Kept subtle so it signals activation without drowning the HUD.
    this.unsubs.push(
      gameEvents.on(GameEvents.ABILITY_ACTIVATED, ({ abilityId }) => {
        if (abilityId !== "auraSight") return;
        const p = this.minna.position;
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        this.burst({
          emitter: new Vector3(p.x, p.y + 0.4, p.z),
          count: 50,
          durationSec: 0.3,
          color1: a11yColor4FromRgb('MINNA_AURA', 0.15, 1.0, 0.6, 0.9, rc),
          color2: a11yColor4FromRgb('BIOLUMINESCENT_BLUE', 0.4, 0.95, 1.0, 0.8, rc),
          colorDead: new Color4(0.02, 0.2, 0.15, 0),
          minSize: 0.05,
          maxSize: 0.14,
          minLifeTime: 0.45,
          maxLifeTime: 0.8,
          minEmitPower: 1.2,
          maxEmitPower: 2.6,
          direction1: new Vector3(-2.0, -0.4, -2.0),
          direction2: new Vector3(2.0, 0.8, 2.0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 400,
        });
      }),
    );

    // Basil — brass bracket snap sparks when Pneumatic Anchor activates.
    this.unsubs.push(
      gameEvents.on(GameEvents.ABILITY_ACTIVATED, ({ abilityId }) => {
        if (abilityId !== "pneumaticAnchor") return;
        const p = this.basil.position;
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        this.burst({
          emitter: new Vector3(p.x, p.y - 0.4, p.z),
          count: 18,
          durationSec: 0.2,
          color1: a11yColor4FromRgb('BRASS', 1.0, 0.78, 0.35, 1.0, rc),
          color2: a11yColor4FromRgb('COPPER', 0.9, 0.55, 0.25, 1.0, rc),
          colorDead: new Color4(0.3, 0.15, 0.05, 0),
          minSize: 0.06,
          maxSize: 0.12,
          minLifeTime: 0.25,
          maxLifeTime: 0.4,
          minEmitPower: 1.6,
          maxEmitPower: 3.2,
          direction1: new Vector3(-1.0, 0.1, -0.3),
          direction2: new Vector3(1.0, 0.6, 0.3),
          gravity: new Vector3(0, -10, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 200,
        });
      }),
    );

    // Minna — soft warm ripple driven by the per-frame purr pulse so
    // the VFX tracks the pulse's actual location, not a debounced
    // transition event. NPC_CALMED stays semantic (one-shot per NPC).
    this.unsubs.push(
      gameEvents.on(GameEvents.PURR_PULSE, (payload) => {
        const src = payload?.sourcePosition ?? this.minna.position;
        this.burst({
          emitter: new Vector3(src.x, src.y + 0.2, src.z),
          count: 18,
          durationSec: 0.35,
          color1: new Color4(1.0, 0.9, 0.7, 0.7),
          color2: new Color4(0.8, 0.65, 0.9, 0.5),
          colorDead: new Color4(0.2, 0.18, 0.3, 0),
          minSize: 0.06,
          maxSize: 0.18,
          minLifeTime: 0.6,
          maxLifeTime: 1.0,
          minEmitPower: 0.4,
          maxEmitPower: 1.2,
          direction1: new Vector3(-1.5, -0.1, -1.5),
          direction2: new Vector3(1.5, 0.6, 1.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 90,
        });
      }),
    );

    // Co-op — echoing green ring of particles on Resonant Drop.
    this.unsubs.push(
      gameEvents.on(GameEvents.RESONANT_DROP_TRIGGERED, (p) => {
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        // Emerald/Green shockwave ring
        this.burst({
          emitter: new Vector3(p.x, p.y, p.z),
          count: 80,
          durationSec: 0.45,
          color1: a11yColor4FromRgb('EMERALD', 0.0, 1.0, 0.4, 1.0, rc),
          color2: a11yColor4FromRgb('BIOLUMINESCENT_GREEN', 0.2, 0.8, 0.3, 0.8, rc),
          colorDead: new Color4(0.0, 0.2, 0.05, 0),
          minSize: 0.08,
          maxSize: 0.22,
          minLifeTime: 0.5,
          maxLifeTime: 0.95,
          minEmitPower: 3.5,
          maxEmitPower: 7.0,
          direction1: new Vector3(-2.5, 0.0, -2.5),
          direction2: new Vector3(2.5, 0.5, 2.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 420,
        });

        // Copper sparks burst (from Basil ground slam)
        this.burst({
          emitter: new Vector3(p.x, p.y, p.z),
          count: 50,
          durationSec: 0.3,
          color1: a11yColor4FromRgb('BRASS', 1.0, 0.7, 0.3, 1.0, rc),
          color2: a11yColor4FromRgb('COPPER', 0.9, 0.4, 0.1, 1.0, rc),
          colorDead: new Color4(0.3, 0.1, 0.02, 0),
          minSize: 0.05,
          maxSize: 0.15,
          minLifeTime: 0.3,
          maxLifeTime: 0.6,
          minEmitPower: 2.5,
          maxEmitPower: 5.5,
          direction1: new Vector3(-2.0, 0.2, -2.0),
          direction2: new Vector3(2.0, 1.5, 2.0),
          gravity: new Vector3(0, -9.81, 0),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 300,
        });

        // Trigger lightweight camera shake (safe, non-game-loop-freezing)
        // Skipped entirely when reducedMotion is enabled (vestibular comfort).
        const reducedForShake = AccessibilitySystem.getInstance().state.reducedMotion;
        const camera: any = this.scene.activeCamera;
        if (!reducedForShake && camera && this.scene.getEngine().name !== "NullEngine") {
          const originalTarget = camera.getTarget().clone();
          let elapsed = 0;
          const duration = 350; // ms
          const intensity = 0.14;

          const shakeObs = this.scene.onBeforeRenderObservable.add(() => {
            const dt = this.scene.getEngine().getDeltaTime();
            elapsed += dt;
            if (elapsed < duration) {
              const offsetX = (Math.random() - 0.5) * intensity;
              const offsetY = (Math.random() - 0.5) * intensity;
              camera.setTarget(originalTarget.add(new Vector3(offsetX, offsetY, 0)));
            } else {
              camera.setTarget(originalTarget);
              this.scene.onBeforeRenderObservable.remove(shakeObs);
            }
          });
        }
      }),
    );

    // Co-op — indigo shimmer when Chronos Bubble opens.
    this.unsubs.push(
      gameEvents.on(GameEvents.CHRONOS_BUBBLE_TRIGGERED, (p) => {
        const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
        // Shimmering time-bubble particle field
        this.burst({
          emitter: new Vector3(p.x, p.y, p.z),
          count: 100,
          durationSec: 0.95,
          color1: a11yColor4FromRgb('BIOLUMINESCENT_PURPLE', 0.3, 0.2, 0.9, 0.9, rc),
          color2: a11yColor4FromRgb('BIOLUMINESCENT_BLUE', 0.1, 0.5, 0.9, 0.75, rc),
          colorDead: new Color4(0.02, 0.02, 0.15, 0),
          minSize: 0.08,
          maxSize: 0.24,
          minLifeTime: 0.9,
          maxLifeTime: 1.8,
          minEmitPower: 0.4,
          maxEmitPower: 1.5,
          direction1: new Vector3(-1.5, -0.8, -1.5),
          direction2: new Vector3(1.5, 1.5, 1.5),
          blendMode: ParticleSystem.BLENDMODE_ADD,
          emitRate: 200,
        });

        // Visual Chronos Sphere (PBR glowing dome mesh scaling up)
        // Omitted when reducedMotion is enabled — purely decorative large visual.
        const reducedForSphere = AccessibilitySystem.getInstance().state.reducedMotion;
        if (!reducedForSphere && this.scene.getEngine().name !== "NullEngine" && typeof MeshBuilder !== "undefined") {
          try {
            const sphere = MeshBuilder.CreateSphere(
              "chronosVisualSphere",
              { diameter: 1.0, segments: 16 },
              this.scene
            );
            sphere.position.set(p.x, p.y, p.z);
            sphere.isPickable = false;

            const mat = new PBRMaterial("chronosVisualMat", this.scene);
            mat.metallic = 0.0;
            mat.roughness = 0.2;
            mat.alpha = 0.12;
            mat.albedoColor = new Color3(0.2, 0.4, 0.9);
            mat.emissiveColor = new Color3(0.1, 0.3, 0.8);
            sphere.material = mat;

            const maxRadius = p.radius ?? 15;
            let elapsed = 0;
            const animDuration = 600; // ms to reach full size
            const totalDuration = 5000; // ms

            const sphereObs = this.scene.onBeforeRenderObservable.add(() => {
              const dt = this.scene.getEngine().getDeltaTime();
              elapsed += dt;

              if (elapsed < animDuration) {
                const ratio = elapsed / animDuration;
                // Ease-out quad scale
                const scale = maxRadius * (2 * ratio - ratio * ratio);
                sphere.scaling.set(scale, scale, scale);
              } else if (elapsed < totalDuration) {
                // Keep scaled and fade out at the end
                const remaining = totalDuration - elapsed;
                if (remaining < 1000) {
                  mat.alpha = 0.12 * (remaining / 1000);
                }
              } else {
                sphere.dispose();
                mat.dispose();
                this.scene.onBeforeRenderObservable.remove(sphereObs);
              }
            });
          } catch (e) {
            console.warn("[AbilityVFX] Chronos Visual Sphere failed:", e);
          }
        }
      }),
    );
  }

  /** Spawn a one-shot particle system and auto-dispose when its lifetime ends. */
  private burst(spec: BurstSpec): void {
    if (this.disposed) return;
    const reduced = AccessibilitySystem.getInstance().state.reducedMotion;
    const factor = particleDensityFactor(reduced);
    const capacity = Math.max(1, Math.ceil(spec.count * factor));
    const ps = new ParticleSystem(`vfx_burst_${this.pool.length}`, capacity, this.scene);
    ps.particleTexture = this.sprite;
    ps.emitter = spec.emitter.clone();
    ps.minEmitBox = new Vector3(-0.05, -0.05, -0.05);
    ps.maxEmitBox = new Vector3(0.05, 0.05, 0.05);
    ps.color1 = spec.color1;
    ps.color2 = spec.color2;
    ps.colorDead = spec.colorDead;
    ps.minSize = spec.minSize;
    ps.maxSize = spec.maxSize;
    ps.minLifeTime = spec.minLifeTime;
    ps.maxLifeTime = spec.maxLifeTime;
    ps.emitRate = spec.emitRate * factor;
    ps.minEmitPower = spec.minEmitPower;
    ps.maxEmitPower = spec.maxEmitPower;
    ps.direction1 = spec.direction1;
    ps.direction2 = spec.direction2;
    if (spec.gravity) ps.gravity = spec.gravity;
    ps.blendMode = spec.blendMode ?? ParticleSystem.BLENDMODE_STANDARD;
    // The emitter stops after `durationSec`; remaining in-flight particles
    // finish their lifetime and the system self-disposes after that.
    ps.targetStopDuration = spec.durationSec;
    ps.disposeOnStop = true;
    ps.start();
    this.pool.push(ps);
    // Lightweight cleanup: cull already-disposed entries so the pool
    // doesn't grow unbounded across a long session. disposeOnStop sets
    // _disposeStop internally; we approximate with the public isReady()
    // + activeCount check.
    if (this.pool.length > 32) {
      this.pool = this.pool.filter((p) => p.getActiveCount() > 0);
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const unsub of this.unsubs) unsub();
    this.unsubs.length = 0;
    for (const ps of this.pool) {
      try { ps.dispose(); } catch { /* ignore */ }
    }
    this.pool.length = 0;
    this.sprite.dispose();
  }
}
